/**
 * Helpers purs + buffer client pour le tracking d'usage.
 * Fire-and-forget : jamais await dans le flux métier.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import {
  USAGE_EVENTS,
  UsageEventMeta,
  UsageEventName,
  UsageEventPayload,
  UsageTrackingContext,
} from '../types/usageTracking';

const FLUSH_INTERVAL_MS = 30_000;
const MAX_BATCH = 40;
const MAX_BUFFER = 200;
const PAGE_VIEW_DEDUPE_MS = 60_000;
const SESSION_STORAGE_KEY = 'nauvatek_usage_session_id';
const SESSION_STARTED_KEY = 'nauvatek_usage_session_started';

const USAGE_EVENT_SET = new Set<string>(USAGE_EVENTS);

/** Routes / préfixes hors tracking business. */
const NON_TRACKABLE_PREFIXES = [
  '/login',
  '/documentation',
  '/super-admin',
  '/pressing',
  '/ramassage',
  '/promo',
  '/page/',
  '/p/',
  '/institut-',
  '/lancement',
  '/unauthorized',
];

export function isUsageEventName(value: string): value is UsageEventName {
  return USAGE_EVENT_SET.has(value);
}

export function normalizeUsagePath(pathname: string): string {
  if (!pathname) return '/';
  const cleaned = pathname.split('?')[0].split('#')[0].trim() || '/';
  // Remplacer les IDs dynamiques par :id pour agréger
  return cleaned
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:id')
    .replace(/\/[a-zA-Z0-9_-]{16,}(?=\/|$)/g, '/:id');
}

export function isTrackableBusinessPath(pathname: string): boolean {
  const path = normalizeUsagePath(pathname);
  if (path === '/' || path === '') return false;
  return !NON_TRACKABLE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(prefix)
  );
}

/** Module métier dérivé du path (pour agrégats Super Admin). */
export function resolveUsageModule(pathname: string): string {
  const path = normalizeUsagePath(pathname);
  if (path.startsWith('/pos')) return 'pos';
  if (path.startsWith('/clients')) return 'clients';
  if (path.startsWith('/appointments') || path.startsWith('/appointment-requests')) {
    return 'appointments';
  }
  if (path.startsWith('/inventory')) return 'inventory';
  if (path.startsWith('/pickups')) return 'pickup';
  if (path.startsWith('/marketing')) return 'marketing';
  if (path.startsWith('/activity')) return 'activity';
  if (path.startsWith('/expenses') || path.startsWith('/expense-notes')) return 'expenses';
  if (path.startsWith('/suppliers')) return 'suppliers';
  if (path.startsWith('/accounting')) return 'accounting';
  if (path.startsWith('/employees') || path.startsWith('/payrolls')) return 'hr';
  if (path.startsWith('/manufacturing')) return 'manufacturing';
  if (path.startsWith('/dashboard') || path.startsWith('/site-stats')) return 'dashboard';
  if (path.startsWith('/settings') || path.startsWith('/mon-')) return 'settings';
  if (path.startsWith('/aide') || path.startsWith('/messages-clients')) return 'support';
  if (path.startsWith('/referents') || path.startsWith('/payment-plans')) return 'sales';
  if (path.startsWith('/services') || path.startsWith('/prestations')) return 'catalog';
  return 'other';
}

export function buildUsageDailyDocId(businessId: string, dateYmd: string): string {
  return `${businessId}_${dateYmd}`;
}

export function toUsageDateYmd(tsMs: number): string {
  return new Date(tsMs).toISOString().slice(0, 10);
}

export function createUsageEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getOrCreateUsageSessionId(): string {
  if (typeof sessionStorage === 'undefined') {
    return createUsageEventId();
  }
  const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;
  const id = createUsageEventId();
  sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  return id;
}

export function hasMarkedSessionStart(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(SESSION_STARTED_KEY) === '1';
}

export function markSessionStart(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(SESSION_STARTED_KEY, '1');
}

export function clearUsageSessionFlags(): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_STARTED_KEY);
}

// --- Buffer runtime ---

let trackingContext: UsageTrackingContext = {
  businessId: null,
  userId: null,
  role: null,
  suppress: false,
};

let buffer: UsageEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;
const lastPageViewAt = new Map<string, number>();

export function setUsageTrackingContext(next: Partial<UsageTrackingContext>): void {
  trackingContext = { ...trackingContext, ...next };
}

export function getUsageTrackingContext(): UsageTrackingContext {
  return { ...trackingContext };
}

function scheduleFlush(): void {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushUsageTracking();
  }, FLUSH_INTERVAL_MS);
}

function shouldDedupePageView(path: string, now: number): boolean {
  const last = lastPageViewAt.get(path) ?? 0;
  if (now - last < PAGE_VIEW_DEDUPE_MS) return true;
  lastPageViewAt.set(path, now);
  return false;
}

export function trackUsage(
  event: UsageEventName,
  options?: { path?: string; meta?: UsageEventMeta; businessId?: string }
): void {
  try {
    if (trackingContext.suppress) return;
    if (!isUsageEventName(event)) return;

    const businessId = options?.businessId ?? trackingContext.businessId;
    const userId = trackingContext.userId;
    if (!businessId || !userId) return;

    const now = Date.now();
    let path = options?.path ? normalizeUsagePath(options.path) : undefined;
    let module: string | undefined;

    if (event === 'page_view') {
      if (!path || !isTrackableBusinessPath(path)) return;
      if (shouldDedupePageView(`${businessId}:${path}`, now)) return;
      module = resolveUsageModule(path);
    }

    const payload: UsageEventPayload = {
      eventId: createUsageEventId(),
      businessId,
      userId,
      role: trackingContext.role ?? undefined,
      event,
      path,
      module,
      meta: options?.meta,
      sessionId: getOrCreateUsageSessionId(),
      ts: now,
    };

    buffer.push(payload);
    if (buffer.length > MAX_BUFFER) {
      buffer = buffer.slice(-MAX_BUFFER);
    }
    if (buffer.length >= MAX_BATCH) {
      void flushUsageTracking();
    } else {
      scheduleFlush();
    }
  } catch {
    // Jamais bloquer l'app
  }
}

export function trackPageView(pathname: string): void {
  trackUsage('page_view', { path: pathname });
}

export async function flushUsageTracking(): Promise<void> {
  if (flushing || buffer.length === 0) return;
  flushing = true;
  const batch = buffer.splice(0, MAX_BATCH);
  try {
    const logUsageEvents = httpsCallable(functions, 'logUsageEvents');
    await logUsageEvents({ events: batch });
  } catch (err) {
    // Remettre en tête (best-effort) sans faire grossir indéfiniment
    buffer = [...batch, ...buffer].slice(0, MAX_BUFFER);
    if (typeof console !== 'undefined') {
      console.warn('[usageTracking] flush failed', err);
    }
  } finally {
    flushing = false;
    if (buffer.length > 0) scheduleFlush();
  }
}

/** À appeler avant logout / unload. */
export function flushUsageTrackingSyncBestEffort(): void {
  if (buffer.length === 0) return;
  void flushUsageTracking();
}

/** Exposé pour tests unitaires du buffer. */
export function __resetUsageTrackingForTests(): void {
  trackingContext = { businessId: null, userId: null, role: null, suppress: false };
  buffer = [];
  flushing = false;
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  lastPageViewAt.clear();
}

export function __getUsageBufferForTests(): UsageEventPayload[] {
  return [...buffer];
}
